import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";

// ============================================================================
// NEXUS ASSISTANT — Complete Application
// ============================================================================

// --- THEME CONTEXT ---
const ThemeContext = createContext();
const useTheme = () => useContext(ThemeContext);

// --- AUTH CONTEXT ---
const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

// ============================================================================
// DATA LAYER — Production-ready structure (mock data for test user)
// ============================================================================

const TEST_USER = {
  id: "usr_001",
  email: "marcus.rivera@nexustest.com",
  password: "NexusTest2025!",
  name: "Marcus Rivera",
  role: "Sales Director",
  company: "TechVenture Solutions",
  avatar: null,
  linkedinConnected: true,
  emailConnected: true,
  onboardingComplete: true,
  createdAt: "2025-02-01T09:00:00Z",
  lastActive: new Date().toISOString(),
  nexusPersonality: { formality: 70, verbosity: 50, proactivity: 80 },
  theme: "midnight",
};

const ADMIN_USER = {
  id: "admin_001",
  email: "admin@nexusassistant.ai",
  password: "NexusAdmin2025!",
  name: "Admin",
  role: "admin",
  isAdmin: true,
};

const CONTACTS = [
  { id: "c1", name: "Sarah Chen", title: "VP Engineering", company: "Meridian Tech", email: "sarah.chen@meridiantech.com", score: 92, trend: "up", lastContact: "2 hours ago", nextTouch: "Follow up on partnership proposal", avatar: "SC", tags: ["Partner", "High Value"], linkedin: true, messages: 24, sentiment: "positive" },
  { id: "c2", name: "James Okonkwo", title: "CEO", company: "Horizon Dynamics", email: "james@horizondynamics.io", score: 87, trend: "up", lastContact: "1 day ago", nextTouch: "Send case study he requested", avatar: "JO", tags: ["Prospect", "Enterprise"], linkedin: true, messages: 18, sentiment: "positive" },
  { id: "c3", name: "Priya Sharma", title: "Head of Sales", company: "NovaBridge", email: "priya.sharma@novabridge.com", score: 74, trend: "stable", lastContact: "3 days ago", nextTouch: "Check in on Q2 planning", avatar: "PS", tags: ["Client", "Renewal"], linkedin: true, messages: 31, sentiment: "neutral" },
  { id: "c4", name: "Alex Torres", title: "Product Manager", company: "Luminary Labs", email: "atorres@luminarylabs.co", score: 68, trend: "down", lastContact: "1 week ago", nextTouch: "Re-engage — relationship cooling", avatar: "AT", tags: ["Prospect"], linkedin: true, messages: 9, sentiment: "neutral" },
  { id: "c5", name: "Diana Blackwell", title: "CTO", company: "Apex Innovations", email: "diana@apexinnovations.com", score: 95, trend: "up", lastContact: "5 hours ago", nextTouch: "Prepare demo for her team Thursday", avatar: "DB", tags: ["Champion", "High Value", "Enterprise"], linkedin: true, messages: 47, sentiment: "positive" },
  { id: "c6", name: "Ryan Mitchell", title: "Director of Partnerships", company: "ScaleForce", email: "ryan.mitchell@scaleforce.io", score: 61, trend: "down", lastContact: "2 weeks ago", nextTouch: "Urgent — hasn't responded to last 2 messages", avatar: "RM", tags: ["At Risk"], linkedin: true, messages: 12, sentiment: "cooling" },
  { id: "c7", name: "Elena Voss", title: "Managing Director", company: "Quantum Reach", email: "evoss@quantumreach.com", score: 83, trend: "up", lastContact: "4 hours ago", nextTouch: "Send contract revision", avatar: "EV", tags: ["Client", "Enterprise"], linkedin: true, messages: 36, sentiment: "positive" },
  { id: "c8", name: "Marcus Kim", title: "Head of Growth", company: "Velocity AI", email: "mkim@velocityai.com", score: 56, trend: "down", lastContact: "3 weeks ago", nextTouch: "Cold — consider re-engagement campaign", avatar: "MK", tags: ["Lost"], linkedin: false, messages: 5, sentiment: "disengaged" },
  { id: "c9", name: "Aisha Patel", title: "VP Sales", company: "CloudNine Solutions", email: "aisha@cloudnine.io", score: 78, trend: "stable", lastContact: "2 days ago", nextTouch: "Discuss referral partnership", avatar: "AP", tags: ["Partner"], linkedin: true, messages: 22, sentiment: "positive" },
  { id: "c10", name: "Thomas Andersen", title: "Founder", company: "Nordic Systems", email: "thomas@nordicsystems.eu", score: 71, trend: "stable", lastContact: "5 days ago", nextTouch: "Follow up on pricing discussion", avatar: "TA", tags: ["Prospect", "SMB"], linkedin: true, messages: 8, sentiment: "neutral" },
  { id: "c11", name: "Camille Dubois", title: "Chief Revenue Officer", company: "Elevate Partners", email: "cdubois@elevatepartners.com", score: 89, trend: "up", lastContact: "6 hours ago", nextTouch: "Prep board presentation materials", avatar: "CD", tags: ["Champion", "High Value"], linkedin: true, messages: 41, sentiment: "positive" },
  { id: "c12", name: "David Nakamura", title: "Strategy Lead", company: "PivotPoint", email: "david.n@pivotpoint.co", score: 45, trend: "down", lastContact: "1 month ago", nextTouch: "Relationship dormant — archive or re-engage?", avatar: "DN", tags: ["Inactive"], linkedin: true, messages: 3, sentiment: "disengaged" },
];

const INBOX_MESSAGES = [
  { id: "m1", from: "Diana Blackwell", channel: "linkedin", subject: "", preview: "Marcus, loved the proposal. Can we get your team on a call Thursday to walk through the integration timeline? My CTO wants to be involved.", time: "5 hours ago", unread: true, flagged: true, contactId: "c5", fullBody: "Marcus, loved the proposal. Can we get your team on a call Thursday to walk through the integration timeline? My CTO wants to be involved. We're looking at a Q2 rollout if the numbers work. Let me know what times work for your side." },
  { id: "m2", from: "Sarah Chen", channel: "email", subject: "Re: Partnership Framework", preview: "Thanks for sending this over so quickly. I've reviewed the terms with our legal team and we have a few minor revisions...", time: "2 hours ago", unread: true, flagged: false, contactId: "c1", fullBody: "Thanks for sending this over so quickly. I've reviewed the terms with our legal team and we have a few minor revisions. Nothing major — mostly around the data sharing clauses in Section 4. Can we hop on a quick 15-min call tomorrow to align? I want to keep momentum on this." },
  { id: "m3", from: "Elena Voss", channel: "email", subject: "Contract Revision Needed", preview: "Hi Marcus, we need to adjust the scope on our current agreement. The expansion into APAC changes our volume projections significantly.", time: "4 hours ago", unread: true, flagged: false, contactId: "c7", fullBody: "Hi Marcus, we need to adjust the scope on our current agreement. The expansion into APAC changes our volume projections significantly. I'd like to discuss updated pricing tiers that reflect our new growth trajectory. Are you free this week?" },
  { id: "m4", from: "Camille Dubois", channel: "linkedin", subject: "", preview: "Quick heads up — our board meeting is next Tuesday. If you can get me those ROI projections by Friday, I can champion the expansion internally.", time: "6 hours ago", unread: true, flagged: true, contactId: "c11", fullBody: "Quick heads up — our board meeting is next Tuesday. If you can get me those ROI projections by Friday, I can champion the expansion internally. This could be a 3x deal size if the board approves the full rollout. Let's make this happen." },
  { id: "m5", from: "James Okonkwo", channel: "email", subject: "Case Study Request", preview: "Marcus, do you have any case studies from the fintech vertical? We're presenting to our investors next month and need social proof.", time: "1 day ago", unread: false, flagged: false, contactId: "c2", fullBody: "Marcus, do you have any case studies from the fintech vertical? We're presenting to our investors next month and need social proof for the platform investment. Ideally something with measurable ROI figures. Thanks!" },
  { id: "m6", from: "Priya Sharma", channel: "linkedin", subject: "", preview: "Hey Marcus! Just saw your post about relationship-driven selling. Totally resonated with our approach here at NovaBridge.", time: "3 days ago", unread: false, flagged: false, contactId: "c3", fullBody: "Hey Marcus! Just saw your post about relationship-driven selling. Totally resonated with our approach here at NovaBridge. Also — we should talk about renewal timing. Our contract is up in 90 days and I want to make sure we're aligned on next steps." },
  { id: "m7", from: "Ryan Mitchell", channel: "email", subject: "Re: Re: Partnership Discussion", preview: "Sorry for the delayed response. Things have been hectic with our Series B. Let me circle back next quarter.", time: "2 weeks ago", unread: false, flagged: false, contactId: "c6", fullBody: "Sorry for the delayed response. Things have been hectic with our Series B. Let me circle back next quarter when things settle down. Still interested in exploring this." },
  { id: "m8", from: "Aisha Patel", channel: "linkedin", subject: "", preview: "Marcus, I have a warm intro for you. My colleague at Zenith Corp is looking for exactly what you offer. Want me to connect you?", time: "2 days ago", unread: false, flagged: true, contactId: "c9", fullBody: "Marcus, I have a warm intro for you. My colleague at Zenith Corp is looking for exactly what you offer. Want me to connect you? She's the VP of Operations and they're actively evaluating vendors this quarter." },
  { id: "m9", from: "Thomas Andersen", channel: "email", subject: "Pricing Follow-up", preview: "Hi Marcus, I discussed your pricing proposal with my co-founder. We're interested but need to understand the SMB tier better.", time: "5 days ago", unread: false, flagged: false, contactId: "c10", fullBody: "Hi Marcus, I discussed your pricing proposal with my co-founder. We're interested but need to understand the SMB tier better. Specifically, what's included in the base package vs. add-ons? Can we schedule a walkthrough?" },
  { id: "m10", from: "Alex Torres", channel: "email", subject: "Quick Question", preview: "Hey, I know we haven't connected in a while. I've moved to a new role and wanted to revisit our earlier conversation about...", time: "1 week ago", unread: false, flagged: false, contactId: "c4", fullBody: "Hey, I know we haven't connected in a while. I've moved to a new role at Luminary Labs as Product Manager and wanted to revisit our earlier conversation about integrating your solution into our workflow tools. Is now a good time to reconnect?" },
];

const NEXUS_BRIEFING = {
  greeting: "Good morning, Marcus.",
  summary: "You have 4 unread messages, 2 flagged as priority. Three relationships need attention today, and there's a time-sensitive opportunity with Elevate Partners.",
  priorities: [
    { icon: "🔴", text: "Camille Dubois needs ROI projections by Friday for her board meeting — this could 3x the deal", action: "Draft projections" },
    { icon: "🟠", text: "Diana Blackwell wants a Thursday call — confirm time and prep integration timeline", action: "Schedule call" },
    { icon: "🟡", text: "Sarah Chen has contract revisions — minor legal tweaks, quick resolution possible", action: "Review changes" },
    { icon: "🔵", text: "Aisha Patel offering a warm intro to Zenith Corp VP — high-value referral opportunity", action: "Accept intro" },
  ],
  relationships: [
    { name: "Ryan Mitchell", status: "Cooling", detail: "No response in 2 weeks. His Series B is consuming bandwidth. Suggest a low-pressure touchpoint in 30 days rather than pushing now." },
    { name: "Marcus Kim", status: "At Risk", detail: "3 weeks silent, relationship score dropped to 56. Consider a personalized re-engagement — reference his recent LinkedIn post about AI scaling." },
    { name: "David Nakamura", status: "Dormant", detail: "1 month with no contact. Score at 45. Recommend archiving or sending a 'thinking of you' note." },
  ],
  insight: "Your response rate this week is 94% — above your 30-day average of 87%. LinkedIn messages are getting 2.3x faster replies than email. Consider shifting more high-priority outreach to LinkedIn DMs.",
};

// ============================================================================
// ICON COMPONENTS
// ============================================================================

const Icons = {
  Inbox: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
  Users: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Bot: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>,
  Settings: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
  Send: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  Search: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Star: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  Flag: ({ size = 16, filled }) => <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
  LinkedIn: ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  Mail: ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  ArrowUp: ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>,
  ArrowDown: ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>,
  Minus: ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Check: ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  X: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Shield: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Moon: ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
  Sun: ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  Eye: ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  EyeOff: ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
  Zap: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  LogOut: ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  Bell: ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  ChevronRight: ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  Archive: ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>,
  Clock: ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  TrendingUp: ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  Sparkles: ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/><path d="M19 14l.9 2.7 2.7.9-2.7.9-.9 2.7-.9-2.7L15.4 17.6l2.7-.9.9-2.7z"/><path d="M5 17l.6 1.8L7.4 19.4l-1.8.6-.6 1.8-.6-1.8L2.6 19.4l1.8-.6.6-1.8z"/></svg>,
};


// ============================================================================
// THEME DEFINITIONS
// ============================================================================

const themes = {
  midnight: {
    name: "Midnight",
    bg: "#06080f",
    bgCard: "#0d1117",
    bgCardHover: "#131a24",
    bgSurface: "#161d2b",
    bgInput: "#0d1117",
    bgSidebar: "#080c14",
    border: "#1c2536",
    borderLight: "#141c2a",
    text: "#e2e8f0",
    textSecondary: "#8892a6",
    textMuted: "#4a5568",
    accent: "#00d4ff",
    accentGlow: "rgba(0,212,255,0.15)",
    accentHover: "#00b8e0",
    accentSecondary: "#7c5cfc",
    accentSecondaryGlow: "rgba(124,92,252,0.15)",
    success: "#34d399",
    warning: "#fbbf24",
    danger: "#f87171",
    dangerBg: "rgba(248,113,113,0.1)",
    scoreBg: "rgba(0,212,255,0.08)",
    flagColor: "#fbbf24",
    unreadDot: "#00d4ff",
    linkedinColor: "#0a66c2",
    gradient: "linear-gradient(135deg, #06080f 0%, #0d1527 50%, #0a1628 100%)",
    cardGlow: "0 0 30px rgba(0,212,255,0.03)",
    inputFocus: "0 0 0 2px rgba(0,212,255,0.3)",
  },
  daylight: {
    name: "Daylight",
    bg: "#f8f9fb",
    bgCard: "#ffffff",
    bgCardHover: "#f3f4f6",
    bgSurface: "#f0f2f5",
    bgInput: "#ffffff",
    bgSidebar: "#ffffff",
    border: "#e2e5ea",
    borderLight: "#eef0f3",
    text: "#111827",
    textSecondary: "#6b7280",
    textMuted: "#9ca3af",
    accent: "#2563eb",
    accentGlow: "rgba(37,99,235,0.1)",
    accentHover: "#1d4ed8",
    accentSecondary: "#7c3aed",
    accentSecondaryGlow: "rgba(124,58,237,0.1)",
    success: "#059669",
    warning: "#d97706",
    danger: "#dc2626",
    dangerBg: "rgba(220,38,38,0.06)",
    scoreBg: "rgba(37,99,235,0.06)",
    flagColor: "#d97706",
    unreadDot: "#2563eb",
    linkedinColor: "#0a66c2",
    gradient: "linear-gradient(135deg, #f8f9fb 0%, #eef2ff 50%, #f0f4ff 100%)",
    cardGlow: "0 1px 3px rgba(0,0,0,0.06)",
    inputFocus: "0 0 0 2px rgba(37,99,235,0.25)",
  },
};


// ============================================================================
// STYLES
// ============================================================================

const getStyles = (t) => `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700&family=JetBrains+Mono:wght@400;500&display=swap');

  * { margin:0; padding:0; box-sizing:border-box; }

  body, html, #root {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    background: ${t.bg};
    color: ${t.text};
    height: 100vh;
    overflow: hidden;
  }

  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: ${t.textMuted}; }

  input, textarea, select {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  }

  ::placeholder { color: ${t.textMuted}; }

  @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideIn { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
  @keyframes glowPulse { 0%,100% { box-shadow: 0 0 20px ${t.accentGlow}; } 50% { box-shadow: 0 0 40px ${t.accentGlow}, 0 0 60px ${t.accentGlow}; } }
  @keyframes ripple { 0% { transform:scale(0.8); opacity:1; } 100% { transform:scale(2.4); opacity:0; } }
  @keyframes typing { 0% { opacity:0.3; } 50% { opacity:1; } 100% { opacity:0.3; } }
  @keyframes scaleIn { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }

  .animate-in { animation: fadeIn 0.3s ease-out forwards; }
  .animate-slide { animation: slideUp 0.4s ease-out forwards; }
  .animate-scale { animation: scaleIn 0.25s ease-out forwards; }
`;

// ============================================================================
// LOGIN SCREEN
// ============================================================================

function LoginScreen({ onLogin }) {
  const t = useTheme();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    setTimeout(() => {
      if (isSignUp) {
        if (!name || !email || !password) { setError("All fields are required"); setLoading(false); return; }
        if (password.length < 8) { setError("Password must be at least 8 characters"); setLoading(false); return; }
        const newUser = {
          id: "usr_" + Date.now(),
          email,
          name,
          role: "",
          company: "",
          onboardingComplete: false,
          linkedinConnected: false,
          emailConnected: false,
          createdAt: new Date().toISOString(),
          lastActive: new Date().toISOString(),
          nexusPersonality: { formality: 70, verbosity: 50, proactivity: 80 },
          theme: "midnight",
        };
        onLogin(newUser);
      } else {
        if (email === TEST_USER.email && password === TEST_USER.password) {
          onLogin(TEST_USER);
        } else if (email === ADMIN_USER.email && password === ADMIN_USER.password) {
          onLogin(ADMIN_USER);
        } else {
          setError("Invalid credentials. Try the test account or sign up.");
        }
      }
      setLoading(false);
    }, 800);
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:t.gradient, padding: "20px" }}>
      <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
        <div style={{ position:"absolute", top:"-20%", right:"-10%", width:"600px", height:"600px", borderRadius:"50%", background: `radial-gradient(circle, ${t.accentGlow} 0%, transparent 70%)`, filter:"blur(80px)" }} />
        <div style={{ position:"absolute", bottom:"-20%", left:"-10%", width:"500px", height:"500px", borderRadius:"50%", background: `radial-gradient(circle, ${t.accentSecondaryGlow} 0%, transparent 70%)`, filter:"blur(80px)" }} />
      </div>

      <div className="animate-scale" style={{ width:"100%", maxWidth:"420px", position:"relative", zIndex:1 }}>
        <div style={{ textAlign:"center", marginBottom:"32px" }}>
          <div style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:"56px", height:"56px", borderRadius:"16px", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, marginBottom:"16px", boxShadow:`0 8px 32px ${t.accentGlow}` }}>
            <Icons.Zap size={28} />
          </div>
          <h1 style={{ fontSize:"28px", fontWeight:700, letterSpacing:"-0.02em", color:t.text }}>Nexus Assistant</h1>
          <p style={{ color:t.textSecondary, marginTop:"6px", fontSize:"14px" }}>Your intelligent relationship command center</p>
        </div>

        <div style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:"16px", padding:"32px", boxShadow:t.cardGlow }}>
          <div style={{ display:"flex", marginBottom:"24px", background:t.bgSurface, borderRadius:"10px", padding:"3px" }}>
            {["Sign In","Sign Up"].map((label, i) => (
              <button key={label} onClick={() => { setIsSignUp(i===1); setError(""); }}
                style={{ flex:1, padding:"10px", borderRadius:"8px", border:"none", cursor:"pointer", fontSize:"13px", fontWeight:600, fontFamily:"inherit",
                  background: (i===0 ? !isSignUp : isSignUp) ? t.accent : "transparent",
                  color: (i===0 ? !isSignUp : isSignUp) ? "#fff" : t.textSecondary,
                  transition:"all 0.2s ease" }}>
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {isSignUp && (
              <div style={{ marginBottom:"16px" }}>
                <label style={{ display:"block", fontSize:"12px", fontWeight:600, color:t.textSecondary, marginBottom:"6px", textTransform:"uppercase", letterSpacing:"0.05em" }}>Full Name</label>
                <input value={name} onChange={e=>setName(e.target.value)} placeholder="Marcus Rivera"
                  style={{ width:"100%", padding:"12px 14px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgInput, color:t.text, fontSize:"14px", outline:"none", transition:"all 0.2s" }}
                  onFocus={e => e.target.style.boxShadow = t.inputFocus} onBlur={e => e.target.style.boxShadow = "none"} />
              </div>
            )}
            <div style={{ marginBottom:"16px" }}>
              <label style={{ display:"block", fontSize:"12px", fontWeight:600, color:t.textSecondary, marginBottom:"6px", textTransform:"uppercase", letterSpacing:"0.05em" }}>Email</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com"
                style={{ width:"100%", padding:"12px 14px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgInput, color:t.text, fontSize:"14px", outline:"none", transition:"all 0.2s" }}
                onFocus={e => e.target.style.boxShadow = t.inputFocus} onBlur={e => e.target.style.boxShadow = "none"} />
            </div>
            <div style={{ marginBottom:"24px" }}>
              <label style={{ display:"block", fontSize:"12px", fontWeight:600, color:t.textSecondary, marginBottom:"6px", textTransform:"uppercase", letterSpacing:"0.05em" }}>Password</label>
              <div style={{ position:"relative" }}>
                <input type={showPw ? "text" : "password"} value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••"
                  style={{ width:"100%", padding:"12px 40px 12px 14px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgInput, color:t.text, fontSize:"14px", outline:"none", transition:"all 0.2s" }}
                  onFocus={e => e.target.style.boxShadow = t.inputFocus} onBlur={e => e.target.style.boxShadow = "none"} />
                <button type="button" onClick={()=>setShowPw(!showPw)} style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:t.textMuted, padding:"2px" }}>
                  {showPw ? <Icons.EyeOff /> : <Icons.Eye />}
                </button>
              </div>
            </div>

            {error && <div style={{ padding:"10px 14px", borderRadius:"8px", background:t.dangerBg, color:t.danger, fontSize:"13px", marginBottom:"16px", border:`1px solid ${t.danger}22` }}>{error}</div>}

            <button type="submit" disabled={loading}
              style={{ width:"100%", padding:"13px", borderRadius:"10px", border:"none", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, color:"#fff", fontSize:"14px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", opacity:loading?0.7:1, transition:"all 0.2s", boxShadow:`0 4px 16px ${t.accentGlow}` }}>
              {loading ? "Authenticating..." : isSignUp ? "Create Account" : "Sign In"}
            </button>
          </form>
        </div>

        <p style={{ textAlign:"center", marginTop:"20px", fontSize:"12px", color:t.textMuted }}>
          Test Account: marcus.rivera@nexustest.com / NexusTest2025!
        </p>
      </div>
    </div>
  );
}


// ============================================================================
// ONBOARDING WIZARD
// ============================================================================

function OnboardingWizard({ user, onComplete }) {
  const t = useTheme();
  const [step, setStep] = useState(0);
  const [linkedinConnected, setLinkedinConnected] = useState(false);
  const [emailConnected, setEmailConnected] = useState(false);
  const [emailProvider, setEmailProvider] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);

  const analysisSteps = [
    "Connecting to your LinkedIn profile...",
    "Scanning your professional network...",
    "Analyzing communication patterns...",
    "Mapping relationship strengths...",
    "Building your User DNA Profile...",
    "Calibrating response style engine...",
    "Generating your first morning briefing...",
    "Nexus is ready."
  ];

  useEffect(() => {
    if (analyzing && analysisStep < analysisSteps.length - 1) {
      const timer = setTimeout(() => setAnalysisStep(s => s + 1), 1200);
      return () => clearTimeout(timer);
    }
    if (analyzing && analysisStep === analysisSteps.length - 1) {
      const timer = setTimeout(() => onComplete(), 2000);
      return () => clearTimeout(timer);
    }
  }, [analyzing, analysisStep]);

  const connectLinkedIn = () => {
    setLinkedinConnected(true);
  };
  const connectEmail = (provider) => {
    setEmailProvider(provider);
    setEmailConnected(true);
  };

  const startAnalysis = () => {
    setStep(3);
    setAnalyzing(true);
  };

  const steps = [
    // Step 0: Welcome
    <div key="welcome" className="animate-slide" style={{ textAlign:"center", maxWidth:"480px" }}>
      <div style={{ width:"72px", height:"72px", borderRadius:"20px", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:"24px", boxShadow:`0 12px 40px ${t.accentGlow}`, animation:"glowPulse 3s ease-in-out infinite" }}>
        <Icons.Zap size={36} />
      </div>
      <h1 style={{ fontSize:"32px", fontWeight:700, marginBottom:"12px", letterSpacing:"-0.02em" }}>Welcome to Nexus, {user.name?.split(" ")[0] || "there"}</h1>
      <p style={{ color:t.textSecondary, fontSize:"16px", lineHeight:"1.6", marginBottom:"32px" }}>
        Let's set up your intelligent command center. Connect your accounts and Nexus will learn your communication style, map your relationships, and become your personal assistant.
      </p>
      <button onClick={() => setStep(1)} style={{ padding:"14px 48px", borderRadius:"12px", border:"none", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, color:"#fff", fontSize:"15px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", boxShadow:`0 4px 20px ${t.accentGlow}` }}>
        Let's Get Started
      </button>
    </div>,

    // Step 1: LinkedIn
    <div key="linkedin" className="animate-slide" style={{ textAlign:"center", maxWidth:"480px" }}>
      <div style={{ width:"64px", height:"64px", borderRadius:"16px", background:"#0a66c2", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:"24px", color:"#fff" }}>
        <Icons.LinkedIn size={32} />
      </div>
      <h2 style={{ fontSize:"24px", fontWeight:700, marginBottom:"8px" }}>Connect LinkedIn</h2>
      <p style={{ color:t.textSecondary, fontSize:"14px", lineHeight:"1.6", marginBottom:"28px" }}>
        Nexus will analyze your profile, network, and messaging patterns to understand your professional world.
      </p>
      {!linkedinConnected ? (
        <button onClick={connectLinkedIn} style={{ padding:"14px 40px", borderRadius:"12px", border:"none", background:"#0a66c2", color:"#fff", fontSize:"14px", fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
          Connect with LinkedIn
        </button>
      ) : (
        <div className="animate-scale">
          <div style={{ display:"inline-flex", alignItems:"center", gap:"10px", padding:"12px 24px", borderRadius:"10px", background:`${t.success}18`, color:t.success, fontWeight:600, fontSize:"14px" }}>
            <Icons.Check size={18} /> LinkedIn Connected
          </div>
          <div style={{ marginTop:"24px" }}>
            <button onClick={() => setStep(2)} style={{ padding:"13px 40px", borderRadius:"12px", border:"none", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, color:"#fff", fontSize:"14px", fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
              Continue
            </button>
          </div>
        </div>
      )}
      <p style={{ marginTop:"20px", fontSize:"12px", color:t.textMuted }}>Step 1 of 3 — We never post or message on your behalf without permission</p>
    </div>,

    // Step 2: Email
    <div key="email" className="animate-slide" style={{ textAlign:"center", maxWidth:"480px" }}>
      <div style={{ width:"64px", height:"64px", borderRadius:"16px", background:t.accent, display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:"24px", color:"#fff" }}>
        <Icons.Mail size={32} />
      </div>
      <h2 style={{ fontSize:"24px", fontWeight:700, marginBottom:"8px" }}>Connect Email</h2>
      <p style={{ color:t.textSecondary, fontSize:"14px", lineHeight:"1.6", marginBottom:"28px" }}>
        Bring your business email into the unified inbox. Nexus analyzes communication patterns to tailor its assistance.
      </p>
      {!emailConnected ? (
        <div style={{ display:"flex", flexDirection:"column", gap:"12px", maxWidth:"300px", margin:"0 auto" }}>
          {[
            { name: "Gmail / Google Workspace", color: "#ea4335", id: "gmail" },
            { name: "Outlook / Microsoft 365", color: "#0078d4", id: "outlook" },
            { name: "Other Email Provider", color: t.textSecondary, id: "other" },
          ].map(p => (
            <button key={p.id} onClick={() => connectEmail(p.id)}
              style={{ padding:"14px 20px", borderRadius:"12px", border:`1px solid ${t.border}`, background:t.bgCard, color:t.text, fontSize:"14px", fontWeight:500, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:"12px", transition:"all 0.2s" }}
              onMouseEnter={e => { e.target.style.borderColor = t.accent; e.target.style.background = t.bgCardHover; }}
              onMouseLeave={e => { e.target.style.borderColor = t.border; e.target.style.background = t.bgCard; }}>
              <span style={{ width:"8px", height:"8px", borderRadius:"50%", background:p.color }} />
              {p.name}
            </button>
          ))}
        </div>
      ) : (
        <div className="animate-scale">
          <div style={{ display:"inline-flex", alignItems:"center", gap:"10px", padding:"12px 24px", borderRadius:"10px", background:`${t.success}18`, color:t.success, fontWeight:600, fontSize:"14px" }}>
            <Icons.Check size={18} /> {emailProvider === "gmail" ? "Gmail" : emailProvider === "outlook" ? "Outlook" : "Email"} Connected
          </div>
          <div style={{ marginTop:"24px" }}>
            <button onClick={startAnalysis} style={{ padding:"13px 40px", borderRadius:"12px", border:"none", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, color:"#fff", fontSize:"14px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", boxShadow:`0 4px 20px ${t.accentGlow}` }}>
              Activate Nexus
            </button>
          </div>
        </div>
      )}
      <p style={{ marginTop:"20px", fontSize:"12px", color:t.textMuted }}>Step 2 of 3 — Read-only access. Nexus never sends emails without your approval.</p>
    </div>,

    // Step 3: Analysis
    <div key="analysis" className="animate-slide" style={{ textAlign:"center", maxWidth:"500px" }}>
      <div style={{ width:"72px", height:"72px", borderRadius:"50%", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:"28px", animation:"glowPulse 2s ease-in-out infinite" }}>
        <Icons.Sparkles size={32} />
      </div>
      <h2 style={{ fontSize:"24px", fontWeight:700, marginBottom:"24px" }}>Nexus is Waking Up</h2>
      <div style={{ textAlign:"left" }}>
        {analysisSteps.map((s, i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:"14px", padding:"10px 0", opacity: i <= analysisStep ? 1 : 0.25, transition:"opacity 0.5s ease", animation: i === analysisStep ? undefined : undefined }}>
            <div style={{ width:"24px", height:"24px", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
              background: i < analysisStep ? t.success : i === analysisStep ? t.accent : t.border,
              transition:"all 0.3s ease" }}>
              {i < analysisStep ? <Icons.Check size={12} /> : i === analysisStep ? <div style={{ width:"8px", height:"8px", borderRadius:"50%", background:"#fff", animation:"pulse 1s ease infinite" }} /> : null}
            </div>
            <span style={{ fontSize:"14px", color: i <= analysisStep ? t.text : t.textMuted, fontWeight: i === analysisStep ? 600 : 400 }}>{s}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop:"24px", height:"4px", background:t.border, borderRadius:"2px", overflow:"hidden" }}>
        <div style={{ height:"100%", background:`linear-gradient(90deg, ${t.accent}, ${t.accentSecondary})`, borderRadius:"2px", transition:"width 0.5s ease", width: `${((analysisStep + 1) / analysisSteps.length) * 100}%` }} />
      </div>
    </div>,
  ];

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:t.gradient, padding:"20px" }}>
      <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
        <div style={{ position:"absolute", top:"10%", right:"5%", width:"400px", height:"400px", borderRadius:"50%", background:`radial-gradient(circle, ${t.accentGlow} 0%, transparent 70%)`, filter:"blur(100px)" }} />
      </div>
      <div style={{ position:"relative", zIndex:1 }}>
        {steps[step]}
      </div>
    </div>
  );
}


// ============================================================================
// UNIFIED INBOX
// ============================================================================

function UnifiedInbox() {
  const t = useTheme();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [showSuggestion, setShowSuggestion] = useState(false);

  const filters = [
    { id: "all", label: "All", count: INBOX_MESSAGES.length },
    { id: "unread", label: "Unread", count: INBOX_MESSAGES.filter(m => m.unread).length },
    { id: "linkedin", label: "LinkedIn", count: INBOX_MESSAGES.filter(m => m.channel === "linkedin").length },
    { id: "email", label: "Email", count: INBOX_MESSAGES.filter(m => m.channel === "email").length },
    { id: "flagged", label: "Flagged", count: INBOX_MESSAGES.filter(m => m.flagged).length },
  ];

  const filtered = INBOX_MESSAGES.filter(m => {
    if (filter === "unread") return m.unread;
    if (filter === "linkedin") return m.channel === "linkedin";
    if (filter === "email") return m.channel === "email";
    if (filter === "flagged") return m.flagged;
    return true;
  }).filter(m => !search || m.from.toLowerCase().includes(search.toLowerCase()) || m.preview.toLowerCase().includes(search.toLowerCase()));

  const suggestions = {
    m1: "Hi Diana, Thursday works perfectly. I'll send over a calendar invite for 2 PM EST with a link for your CTO to join. I'll have the integration timeline and technical specs ready to walk through. Looking forward to it!",
    m2: "Hi Sarah, great to hear the review went smoothly. I'm free tomorrow at 11 AM or 2 PM — either works for a quick alignment call on the Section 4 revisions. Let's keep this moving!",
    m3: "Elena, absolutely — the APAC expansion is exciting news. I'll pull together updated pricing tiers that account for the increased volume. How does Wednesday afternoon look for a walkthrough?",
    m4: "Camille, consider it done. I'll have the ROI projections with full breakdowns on your desk by end of day Thursday, giving you Friday to review before the board meeting. Let's land this.",
  };

  return (
    <div style={{ display:"flex", height:"100%", overflow:"hidden" }}>
      {/* Message List */}
      <div style={{ width: selectedMsg ? "380px" : "100%", borderRight: selectedMsg ? `1px solid ${t.border}` : "none", display:"flex", flexDirection:"column", transition:"width 0.3s ease", flexShrink:0 }}>
        {/* Search & Filters */}
        <div style={{ padding:"20px 20px 0" }}>
          <div style={{ position:"relative", marginBottom:"16px" }}>
            <Icons.Search size={16} />
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search messages..."
              style={{ width:"100%", padding:"10px 14px 10px 38px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgInput, color:t.text, fontSize:"13px", outline:"none" }}
              onFocus={e => e.target.style.boxShadow = t.inputFocus} onBlur={e => e.target.style.boxShadow = "none"} />
            <span style={{ position:"absolute", left:"12px", top:"50%", transform:"translateY(-50%)", color:t.textMuted }}><Icons.Search size={16} /></span>
          </div>
          <div style={{ display:"flex", gap:"6px", overflowX:"auto", paddingBottom:"12px" }}>
            {filters.map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                style={{ padding:"6px 14px", borderRadius:"8px", border:`1px solid ${filter === f.id ? t.accent : t.border}`, background: filter === f.id ? t.accentGlow : "transparent", color: filter === f.id ? t.accent : t.textSecondary, fontSize:"12px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:"6px" }}>
                {f.label}
                {f.count > 0 && <span style={{ background: filter === f.id ? t.accent : t.border, color: filter === f.id ? "#fff" : t.textSecondary, borderRadius:"6px", padding:"1px 6px", fontSize:"10px", fontWeight:700 }}>{f.count}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex:1, overflow:"auto", padding:"8px 12px" }}>
          {filtered.map((msg, i) => (
            <div key={msg.id} onClick={() => { setSelectedMsg(msg); setShowSuggestion(false); setReplyText(""); }}
              className="animate-in" style={{ animationDelay:`${i * 40}ms`, padding:"14px 16px", borderRadius:"12px", cursor:"pointer", marginBottom:"4px", border:`1px solid ${selectedMsg?.id === msg.id ? t.accent + "44" : "transparent"}`,
                background: selectedMsg?.id === msg.id ? t.accentGlow : msg.unread ? `${t.accent}06` : "transparent",
                transition:"all 0.15s ease" }}
              onMouseEnter={e => { if (selectedMsg?.id !== msg.id) e.currentTarget.style.background = t.bgCardHover; }}
              onMouseLeave={e => { if (selectedMsg?.id !== msg.id) e.currentTarget.style.background = msg.unread ? `${t.accent}06` : "transparent"; }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:"12px" }}>
                <div style={{ width:"40px", height:"40px", borderRadius:"10px", background:`linear-gradient(135deg, ${t.accent}30, ${t.accentSecondary}30)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"13px", fontWeight:700, color:t.accent, flexShrink:0, position:"relative" }}>
                  {msg.from.split(" ").map(w => w[0]).join("")}
                  {msg.unread && <div style={{ position:"absolute", top:"-2px", right:"-2px", width:"8px", height:"8px", borderRadius:"50%", background:t.unreadDot, border:`2px solid ${t.bg}` }} />}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"4px" }}>
                    <span style={{ fontWeight: msg.unread ? 700 : 500, fontSize:"13px", color:t.text }}>{msg.from}</span>
                    <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
                      {msg.flagged && <span style={{ color:t.flagColor }}><Icons.Flag size={12} filled /></span>}
                      <span style={{ color:msg.channel === "linkedin" ? t.linkedinColor : t.textMuted }}>{msg.channel === "linkedin" ? <Icons.LinkedIn size={12} /> : <Icons.Mail size={12} />}</span>
                    </div>
                  </div>
                  {msg.subject && <div style={{ fontSize:"12px", fontWeight:600, color:t.text, marginBottom:"3px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{msg.subject}</div>}
                  <p style={{ fontSize:"12px", color:t.textSecondary, lineHeight:"1.4", overflow:"hidden", textOverflow:"ellipsis", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{msg.preview}</p>
                  <span style={{ fontSize:"11px", color:t.textMuted, marginTop:"4px", display:"block" }}>{msg.time}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Message Detail */}
      {selectedMsg && (
        <div className="animate-in" style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          <div style={{ padding:"20px 24px", borderBottom:`1px solid ${t.border}`, display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"4px" }}>
                <h3 style={{ fontSize:"16px", fontWeight:700 }}>{selectedMsg.from}</h3>
                <span style={{ color:selectedMsg.channel === "linkedin" ? t.linkedinColor : t.textMuted, display:"flex", alignItems:"center", gap:"4px", fontSize:"11px", fontWeight:600, padding:"3px 8px", borderRadius:"6px", background:selectedMsg.channel === "linkedin" ? `${t.linkedinColor}18` : `${t.textMuted}18` }}>
                  {selectedMsg.channel === "linkedin" ? <><Icons.LinkedIn size={11} /> LinkedIn</> : <><Icons.Mail size={11} /> Email</>}
                </span>
              </div>
              {selectedMsg.subject && <p style={{ fontSize:"14px", color:t.textSecondary }}>{selectedMsg.subject}</p>}
              <p style={{ fontSize:"11px", color:t.textMuted, marginTop:"4px" }}>{selectedMsg.time}</p>
            </div>
            <button onClick={() => setSelectedMsg(null)} style={{ background:"none", border:"none", cursor:"pointer", color:t.textMuted, padding:"4px" }}>
              <Icons.X size={18} />
            </button>
          </div>

          <div style={{ flex:1, overflow:"auto", padding:"24px" }}>
            <p style={{ fontSize:"14px", lineHeight:"1.7", color:t.text }}>{selectedMsg.fullBody}</p>

            {suggestions[selectedMsg.id] && !showSuggestion && (
              <button onClick={() => { setShowSuggestion(true); setReplyText(suggestions[selectedMsg.id]); }}
                style={{ marginTop:"24px", padding:"10px 18px", borderRadius:"10px", border:`1px dashed ${t.accent}44`, background:t.accentGlow, color:t.accent, fontSize:"13px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:"8px" }}>
                <Icons.Sparkles size={16} /> Nexus Suggested Reply
              </button>
            )}
          </div>

          {/* Reply Box */}
          <div style={{ padding:"16px 24px", borderTop:`1px solid ${t.border}`, background:t.bgCard }}>
            {showSuggestion && (
              <div className="animate-in" style={{ padding:"10px 14px", borderRadius:"8px", background:t.accentGlow, border:`1px solid ${t.accent}33`, marginBottom:"10px", display:"flex", alignItems:"center", gap:"8px", fontSize:"12px", color:t.accent, fontWeight:500 }}>
                <Icons.Sparkles size={14} /> Nexus drafted this reply in your communication style
              </div>
            )}
            <div style={{ display:"flex", gap:"10px" }}>
              <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..."
                style={{ flex:1, padding:"12px 14px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgInput, color:t.text, fontSize:"13px", outline:"none", resize:"none", minHeight:"44px", maxHeight:"120px", fontFamily:"inherit", lineHeight:"1.5" }}
                onFocus={e => e.target.style.boxShadow = t.inputFocus} onBlur={e => e.target.style.boxShadow = "none"} />
              <button style={{ padding:"12px 16px", borderRadius:"10px", border:"none", background:replyText ? `linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})` : t.border, color:replyText ? "#fff" : t.textMuted, cursor: replyText ? "pointer" : "default", flexShrink:0 }}>
                <Icons.Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// CONTACTS / CONTACT INTELLIGENCE
// ============================================================================

function ContactsView() {
  const t = useTheme();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("score");
  const [selectedContact, setSelectedContact] = useState(null);
  const [filterTag, setFilterTag] = useState("all");

  const allTags = ["all", ...new Set(CONTACTS.flatMap(c => c.tags))];

  const sorted = [...CONTACTS]
    .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.company.toLowerCase().includes(search.toLowerCase()))
    .filter(c => filterTag === "all" || c.tags.includes(filterTag))
    .sort((a, b) => sortBy === "score" ? b.score - a.score : sortBy === "name" ? a.name.localeCompare(b.name) : 0);

  const getScoreColor = (score) => {
    if (score >= 80) return t.success;
    if (score >= 60) return t.warning;
    return t.danger;
  };

  const TrendIcon = ({ trend }) => {
    if (trend === "up") return <span style={{ color:t.success }}><Icons.ArrowUp /></span>;
    if (trend === "down") return <span style={{ color:t.danger }}><Icons.ArrowDown /></span>;
    return <span style={{ color:t.textMuted }}><Icons.Minus /></span>;
  };

  return (
    <div style={{ display:"flex", height:"100%", overflow:"hidden" }}>
      {/* Contact List */}
      <div style={{ width: selectedContact ? "420px" : "100%", borderRight: selectedContact ? `1px solid ${t.border}` : "none", display:"flex", flexDirection:"column", flexShrink:0 }}>
        <div style={{ padding:"20px 20px 0" }}>
          <div style={{ display:"flex", gap:"10px", marginBottom:"14px" }}>
            <div style={{ flex:1, position:"relative" }}>
              <span style={{ position:"absolute", left:"12px", top:"50%", transform:"translateY(-50%)", color:t.textMuted }}><Icons.Search size={15} /></span>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search contacts..."
                style={{ width:"100%", padding:"10px 14px 10px 36px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgInput, color:t.text, fontSize:"13px", outline:"none" }}
                onFocus={e => e.target.style.boxShadow = t.inputFocus} onBlur={e => e.target.style.boxShadow = "none"} />
            </div>
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
              style={{ padding:"10px 12px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgInput, color:t.text, fontSize:"12px", cursor:"pointer", fontFamily:"inherit" }}>
              <option value="score">Sort: Score</option>
              <option value="name">Sort: Name</option>
            </select>
          </div>
          <div style={{ display:"flex", gap:"5px", overflowX:"auto", paddingBottom:"12px" }}>
            {allTags.map(tag => (
              <button key={tag} onClick={() => setFilterTag(tag)}
                style={{ padding:"5px 12px", borderRadius:"7px", border:`1px solid ${filterTag === tag ? t.accent : t.border}`, background: filterTag === tag ? t.accentGlow : "transparent", color: filterTag === tag ? t.accent : t.textSecondary, fontSize:"11px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", textTransform:"capitalize" }}>
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex:1, overflow:"auto", padding:"4px 12px" }}>
          {sorted.map((c, i) => (
            <div key={c.id} onClick={() => setSelectedContact(c)}
              className="animate-in" style={{ animationDelay:`${i * 30}ms`, padding:"14px 16px", borderRadius:"12px", cursor:"pointer", marginBottom:"4px",
                border:`1px solid ${selectedContact?.id === c.id ? t.accent + "44" : "transparent"}`,
                background: selectedContact?.id === c.id ? t.accentGlow : "transparent", transition:"all 0.15s ease" }}
              onMouseEnter={e => { if (selectedContact?.id !== c.id) e.currentTarget.style.background = t.bgCardHover; }}
              onMouseLeave={e => { if (selectedContact?.id !== c.id) e.currentTarget.style.background = "transparent"; }}>
              <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
                <div style={{ position:"relative" }}>
                  <div style={{ width:"44px", height:"44px", borderRadius:"12px", background:`linear-gradient(135deg, ${getScoreColor(c.score)}30, ${getScoreColor(c.score)}10)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"13px", fontWeight:700, color:getScoreColor(c.score) }}>
                    {c.avatar}
                  </div>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"2px" }}>
                    <span style={{ fontWeight:600, fontSize:"13px" }}>{c.name}</span>
                    <TrendIcon trend={c.trend} />
                    {c.linkedin && <span style={{ color:t.linkedinColor }}><Icons.LinkedIn size={11} /></span>}
                  </div>
                  <p style={{ fontSize:"12px", color:t.textSecondary, marginBottom:"2px" }}>{c.title} · {c.company}</p>
                  <p style={{ fontSize:"11px", color:t.textMuted }}>{c.lastContact}</p>
                </div>
                <div style={{ textAlign:"center", flexShrink:0 }}>
                  <div style={{ fontSize:"20px", fontWeight:800, color:getScoreColor(c.score), fontFamily:"'JetBrains Mono', monospace" }}>{c.score}</div>
                  <div style={{ fontSize:"9px", color:t.textMuted, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>Score</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Detail */}
      {selectedContact && (
        <div className="animate-in" style={{ flex:1, overflow:"auto", padding:"24px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"24px" }}>
            <div style={{ display:"flex", gap:"16px", alignItems:"center" }}>
              <div style={{ width:"60px", height:"60px", borderRadius:"16px", background:`linear-gradient(135deg, ${getScoreColor(selectedContact.score)}30, ${getScoreColor(selectedContact.score)}10)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"18px", fontWeight:700, color:getScoreColor(selectedContact.score) }}>
                {selectedContact.avatar}
              </div>
              <div>
                <h2 style={{ fontSize:"20px", fontWeight:700, marginBottom:"4px" }}>{selectedContact.name}</h2>
                <p style={{ fontSize:"14px", color:t.textSecondary }}>{selectedContact.title} · {selectedContact.company}</p>
                <p style={{ fontSize:"12px", color:t.textMuted, marginTop:"4px" }}>{selectedContact.email}</p>
              </div>
            </div>
            <button onClick={() => setSelectedContact(null)} style={{ background:"none", border:"none", cursor:"pointer", color:t.textMuted }}><Icons.X size={18} /></button>
          </div>

          {/* Score Card */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:"12px", marginBottom:"24px" }}>
            {[
              { label: "Relationship Score", value: selectedContact.score, color: getScoreColor(selectedContact.score) },
              { label: "Messages", value: selectedContact.messages, color: t.accent },
              { label: "Sentiment", value: selectedContact.sentiment, color: selectedContact.sentiment === "positive" ? t.success : selectedContact.sentiment === "neutral" ? t.warning : t.danger },
              { label: "Last Contact", value: selectedContact.lastContact, color: t.textSecondary },
            ].map((stat, i) => (
              <div key={i} style={{ padding:"16px", borderRadius:"12px", background:t.bgCard, border:`1px solid ${t.border}` }}>
                <div style={{ fontSize:"10px", color:t.textMuted, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:"6px" }}>{stat.label}</div>
                <div style={{ fontSize: typeof stat.value === "number" ? "24px" : "14px", fontWeight:700, color:stat.color, fontFamily: typeof stat.value === "number" ? "'JetBrains Mono', monospace" : "inherit", textTransform:"capitalize" }}>{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Tags */}
          <div style={{ display:"flex", gap:"6px", marginBottom:"20px", flexWrap:"wrap" }}>
            {selectedContact.tags.map(tag => (
              <span key={tag} style={{ padding:"4px 12px", borderRadius:"6px", background:t.accentGlow, color:t.accent, fontSize:"11px", fontWeight:600 }}>{tag}</span>
            ))}
          </div>

          {/* Nexus Insight */}
          <div style={{ padding:"18px", borderRadius:"12px", background:`linear-gradient(135deg, ${t.accentGlow}, ${t.accentSecondaryGlow})`, border:`1px solid ${t.accent}22`, marginBottom:"20px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" }}>
              <Icons.Sparkles size={16} />
              <span style={{ fontSize:"12px", fontWeight:700, color:t.accent, textTransform:"uppercase", letterSpacing:"0.05em" }}>Nexus Insight</span>
            </div>
            <p style={{ fontSize:"13px", color:t.text, lineHeight:"1.6" }}>{selectedContact.nextTouch}</p>
          </div>

          {/* Actions */}
          <div style={{ display:"flex", gap:"10px" }}>
            {["Send Message", "Schedule Follow-up", "View History"].map(action => (
              <button key={action} style={{ padding:"10px 18px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgCard, color:t.text, fontSize:"12px", fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                {action}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// NEXUS AI AGENT
// ============================================================================

function NexusAgent() {
  const t = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [showBriefing, setShowBriefing] = useState(true);
  const [typing, setTyping] = useState(false);
  const chatEndRef = useRef(null);

  const scrollToBottom = () => chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(scrollToBottom, [messages]);

  const nexusResponses = {
    "draft": "Here's a draft reply based on your usual communication style with this contact:\n\n\"Hi [Name], thanks for reaching out. I've reviewed the details and think we're aligned on the key points. Let me pull together a more detailed breakdown by end of week so we can move forward with confidence. Looking forward to connecting.\"\n\nWant me to adjust the tone or add specific details?",
    "reach": "Based on your relationship scores and activity patterns, here's who I'd prioritize this week:\n\n1. **Ryan Mitchell** (Score: 61, declining) — His Series B is consuming bandwidth, but a quick congratulatory note would keep the door open without pressure.\n\n2. **Marcus Kim** (Score: 56, declining) — He posted about AI scaling on LinkedIn yesterday. A thoughtful comment could re-ignite the conversation.\n\n3. **Thomas Andersen** (Score: 71, stable) — He's waiting on pricing clarification. Quick response could push him to decision.\n\nWant me to draft messages for any of these?",
    "summarize": "Here's your relationship overview with the contacts you interact with most:\n\n**Strongest Relationships:** Diana Blackwell (95), Sarah Chen (92), Camille Dubois (89) — these are your champions. They respond quickly and advocate for you internally.\n\n**Growing:** James Okonkwo (87 ↑), Elena Voss (83 ↑) — momentum is building. Keep the engagement frequency up.\n\n**Needs Attention:** Alex Torres (68 ↓), Ryan Mitchell (61 ↓) — both trending down. Different strategies needed for each.\n\n**At Risk:** Marcus Kim (56 ↓), David Nakamura (45 ↓) — these relationships are going dormant. Decide: re-engage or archive.",
    "default": "I've analyzed the context of your request. Based on your communication patterns and relationship data, here's my recommendation:\n\nYour most effective outreach happens between 9-11 AM on Tuesdays and Thursdays — that's when your contacts are most responsive. LinkedIn DMs get 2.3x faster replies than email for your network.\n\nWould you like me to draft something specific, analyze a relationship, or help prioritize your outreach for today?"
  };

  const sendMessage = () => {
    if (!input.trim()) return;
    const userMsg = { role: "user", content: input, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setTyping(true);
    setShowBriefing(false);

    setTimeout(() => {
      const lower = input.toLowerCase();
      let response = nexusResponses.default;
      if (lower.includes("draft") || lower.includes("reply") || lower.includes("write")) response = nexusResponses.draft;
      else if (lower.includes("reach") || lower.includes("who") || lower.includes("priorit") || lower.includes("contact")) response = nexusResponses.reach;
      else if (lower.includes("summar") || lower.includes("overview") || lower.includes("relationship")) response = nexusResponses.summarize;

      setMessages(prev => [...prev, { role: "nexus", content: response, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
      setTyping(false);
    }, 1500 + Math.random() * 1000);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      {/* Chat Area */}
      <div style={{ flex:1, overflow:"auto", padding:"24px" }}>
        {showBriefing && (
          <div className="animate-slide" style={{ maxWidth:"640px", margin:"0 auto" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"14px", marginBottom:"24px" }}>
              <div style={{ width:"48px", height:"48px", borderRadius:"14px", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:`0 4px 20px ${t.accentGlow}` }}>
                <Icons.Sparkles size={22} />
              </div>
              <div>
                <h2 style={{ fontSize:"18px", fontWeight:700 }}>{NEXUS_BRIEFING.greeting}</h2>
                <p style={{ fontSize:"13px", color:t.textSecondary }}>Here's your morning briefing</p>
              </div>
            </div>

            <p style={{ fontSize:"14px", color:t.textSecondary, lineHeight:"1.6", marginBottom:"20px" }}>{NEXUS_BRIEFING.summary}</p>

            {/* Priority Actions */}
            <div style={{ marginBottom:"24px" }}>
              <h3 style={{ fontSize:"12px", fontWeight:700, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"12px" }}>Priority Actions</h3>
              {NEXUS_BRIEFING.priorities.map((p, i) => (
                <div key={i} className="animate-in" style={{ animationDelay:`${i * 80}ms`, padding:"14px 16px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgCard, marginBottom:"8px", display:"flex", alignItems:"flex-start", gap:"12px" }}>
                  <span style={{ fontSize:"16px", flexShrink:0, marginTop:"1px" }}>{p.icon}</span>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:"13px", lineHeight:"1.5", color:t.text }}>{p.text}</p>
                  </div>
                  <button style={{ padding:"6px 14px", borderRadius:"8px", border:`1px solid ${t.accent}44`, background:t.accentGlow, color:t.accent, fontSize:"11px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", flexShrink:0 }}>
                    {p.action}
                  </button>
                </div>
              ))}
            </div>

            {/* Relationship Alerts */}
            <div style={{ marginBottom:"24px" }}>
              <h3 style={{ fontSize:"12px", fontWeight:700, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"12px" }}>Relationships to Watch</h3>
              {NEXUS_BRIEFING.relationships.map((r, i) => (
                <div key={i} style={{ padding:"14px 16px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgCard, marginBottom:"8px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
                    <span style={{ fontWeight:700, fontSize:"13px" }}>{r.name}</span>
                    <span style={{ padding:"2px 8px", borderRadius:"6px", fontSize:"10px", fontWeight:700,
                      background: r.status === "Cooling" ? `${t.warning}20` : r.status === "At Risk" ? `${t.danger}20` : `${t.textMuted}20`,
                      color: r.status === "Cooling" ? t.warning : r.status === "At Risk" ? t.danger : t.textMuted }}>{r.status}</span>
                  </div>
                  <p style={{ fontSize:"12px", color:t.textSecondary, lineHeight:"1.5" }}>{r.detail}</p>
                </div>
              ))}
            </div>

            {/* Insight */}
            <div style={{ padding:"16px 18px", borderRadius:"12px", background:`linear-gradient(135deg, ${t.accentGlow}, ${t.accentSecondaryGlow})`, border:`1px solid ${t.accent}22` }}>
              <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
                <Icons.TrendingUp size={14} />
                <span style={{ fontSize:"11px", fontWeight:700, color:t.accent, textTransform:"uppercase", letterSpacing:"0.05em" }}>Performance Insight</span>
              </div>
              <p style={{ fontSize:"13px", color:t.text, lineHeight:"1.6" }}>{NEXUS_BRIEFING.insight}</p>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        {messages.map((msg, i) => (
          <div key={i} className="animate-in" style={{ display:"flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", marginBottom:"16px", maxWidth:"700px", margin: "0 auto 16px" }}>
            <div style={{ maxWidth:"85%", display:"flex", gap:"10px", flexDirection: msg.role === "user" ? "row-reverse" : "row" }}>
              {msg.role === "nexus" && (
                <div style={{ width:"32px", height:"32px", borderRadius:"10px", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <Icons.Sparkles size={16} />
                </div>
              )}
              <div style={{ padding:"14px 18px", borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                background: msg.role === "user" ? `linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})` : t.bgCard,
                color: msg.role === "user" ? "#fff" : t.text,
                border: msg.role === "nexus" ? `1px solid ${t.border}` : "none" }}>
                <p style={{ fontSize:"13px", lineHeight:"1.7", whiteSpace:"pre-wrap" }}>{msg.content}</p>
                <span style={{ fontSize:"10px", opacity:0.5, marginTop:"6px", display:"block" }}>{msg.time}</span>
              </div>
            </div>
          </div>
        ))}

        {typing && (
          <div style={{ display:"flex", gap:"10px", maxWidth:"700px", margin:"0 auto" }}>
            <div style={{ width:"32px", height:"32px", borderRadius:"10px", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Icons.Sparkles size={16} />
            </div>
            <div style={{ padding:"14px 18px", borderRadius:"16px 16px 16px 4px", background:t.bgCard, border:`1px solid ${t.border}`, display:"flex", gap:"6px" }}>
              {[0,1,2].map(i => <div key={i} style={{ width:"7px", height:"7px", borderRadius:"50%", background:t.accent, animation:`typing 1.2s ease infinite ${i * 0.2}s` }} />)}
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding:"16px 24px", borderTop:`1px solid ${t.border}`, background:t.bgCard }}>
        <div style={{ maxWidth:"700px", margin:"0 auto" }}>
          {showBriefing && (
            <div style={{ display:"flex", gap:"8px", marginBottom:"12px", overflowX:"auto" }}>
              {["Who should I reach out to?", "Draft a reply", "Summarize my relationships"].map(q => (
                <button key={q} onClick={() => { setInput(q); }}
                  style={{ padding:"8px 16px", borderRadius:"20px", border:`1px solid ${t.border}`, background:t.bgSurface, color:t.textSecondary, fontSize:"12px", cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                  {q}
                </button>
              ))}
            </div>
          )}
          <div style={{ display:"flex", gap:"10px" }}>
            <input value={input} onChange={e=>setInput(e.target.value)} placeholder="Ask Nexus anything..."
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              style={{ flex:1, padding:"13px 16px", borderRadius:"12px", border:`1px solid ${t.border}`, background:t.bgInput, color:t.text, fontSize:"14px", outline:"none", fontFamily:"inherit" }}
              onFocus={e => e.target.style.boxShadow = t.inputFocus} onBlur={e => e.target.style.boxShadow = "none"} />
            <button onClick={sendMessage} style={{ padding:"13px 18px", borderRadius:"12px", border:"none", background: input ? `linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})` : t.border, color: input ? "#fff" : t.textMuted, cursor: input ? "pointer" : "default", flexShrink:0 }}>
              <Icons.Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// SETTINGS
// ============================================================================

function SettingsView({ user, onLogout, onThemeChange, currentTheme }) {
  const t = useTheme();
  const [personality, setPersonality] = useState(user.nexusPersonality || { formality: 70, verbosity: 50, proactivity: 80 });

  const Slider = ({ label, value, onChange, leftLabel, rightLabel }) => (
    <div style={{ marginBottom:"20px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"8px" }}>
        <span style={{ fontSize:"13px", fontWeight:600, color:t.text }}>{label}</span>
        <span style={{ fontSize:"12px", color:t.accent, fontFamily:"'JetBrains Mono', monospace" }}>{value}%</span>
      </div>
      <input type="range" min="0" max="100" value={value} onChange={e => onChange(Number(e.target.value))}
        style={{ width:"100%", height:"6px", borderRadius:"3px", appearance:"none", background:`linear-gradient(to right, ${t.accent} ${value}%, ${t.border} ${value}%)`, cursor:"pointer", outline:"none" }} />
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:"4px" }}>
        <span style={{ fontSize:"11px", color:t.textMuted }}>{leftLabel}</span>
        <span style={{ fontSize:"11px", color:t.textMuted }}>{rightLabel}</span>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth:"640px", margin:"0 auto", padding:"24px", overflow:"auto", height:"100%" }}>
      <h2 style={{ fontSize:"20px", fontWeight:700, marginBottom:"24px" }}>Settings</h2>

      {/* Profile */}
      <div style={{ padding:"20px", borderRadius:"14px", background:t.bgCard, border:`1px solid ${t.border}`, marginBottom:"16px" }}>
        <h3 style={{ fontSize:"13px", fontWeight:700, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"16px" }}>Profile</h3>
        <div style={{ display:"flex", alignItems:"center", gap:"16px" }}>
          <div style={{ width:"56px", height:"56px", borderRadius:"16px", background:`linear-gradient(135deg, ${t.accent}30, ${t.accentSecondary}30)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"18px", fontWeight:700, color:t.accent }}>
            {user.name?.split(" ").map(w => w[0]).join("") || "?"}
          </div>
          <div>
            <p style={{ fontWeight:700, fontSize:"15px" }}>{user.name || "New User"}</p>
            <p style={{ fontSize:"13px", color:t.textSecondary }}>{user.role && user.company ? `${user.role} · ${user.company}` : user.email}</p>
          </div>
        </div>
      </div>

      {/* Theme */}
      <div style={{ padding:"20px", borderRadius:"14px", background:t.bgCard, border:`1px solid ${t.border}`, marginBottom:"16px" }}>
        <h3 style={{ fontSize:"13px", fontWeight:700, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"16px" }}>Appearance</h3>
        <div style={{ display:"flex", gap:"12px" }}>
          {["midnight", "daylight"].map(th => (
            <button key={th} onClick={() => onThemeChange(th)}
              style={{ flex:1, padding:"16px", borderRadius:"12px", border:`2px solid ${currentTheme === th ? t.accent : t.border}`, background: currentTheme === th ? t.accentGlow : t.bgSurface, cursor:"pointer", textAlign:"center", transition:"all 0.2s" }}>
              <div style={{ marginBottom:"8px" }}>{th === "midnight" ? <Icons.Moon size={22} /> : <Icons.Sun size={22} />}</div>
              <span style={{ fontSize:"13px", fontWeight:600, color: currentTheme === th ? t.accent : t.textSecondary, textTransform:"capitalize" }}>{th}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Connected Accounts */}
      <div style={{ padding:"20px", borderRadius:"14px", background:t.bgCard, border:`1px solid ${t.border}`, marginBottom:"16px" }}>
        <h3 style={{ fontSize:"13px", fontWeight:700, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"16px" }}>Connected Accounts</h3>
        {[
          { name: "LinkedIn", connected: user.linkedinConnected, icon: <Icons.LinkedIn size={18} />, color: "#0a66c2" },
          { name: "Email", connected: user.emailConnected, icon: <Icons.Mail size={18} />, color: t.accent },
        ].map(acc => (
          <div key={acc.name} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 0", borderBottom:`1px solid ${t.borderLight}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
              <span style={{ color:acc.color }}>{acc.icon}</span>
              <span style={{ fontSize:"14px", fontWeight:500 }}>{acc.name}</span>
            </div>
            <span style={{ fontSize:"12px", fontWeight:600, color: acc.connected ? t.success : t.textMuted, padding:"4px 12px", borderRadius:"6px", background: acc.connected ? `${t.success}18` : `${t.textMuted}18` }}>
              {acc.connected ? "Connected" : "Not Connected"}
            </span>
          </div>
        ))}
      </div>

      {/* Nexus Personality */}
      <div style={{ padding:"20px", borderRadius:"14px", background:t.bgCard, border:`1px solid ${t.border}`, marginBottom:"16px" }}>
        <h3 style={{ fontSize:"13px", fontWeight:700, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"16px" }}>Nexus Personality</h3>
        <Slider label="Formality" value={personality.formality} onChange={v => setPersonality({...personality, formality: v})} leftLabel="Casual" rightLabel="Professional" />
        <Slider label="Verbosity" value={personality.verbosity} onChange={v => setPersonality({...personality, verbosity: v})} leftLabel="Concise" rightLabel="Detailed" />
        <Slider label="Proactivity" value={personality.proactivity} onChange={v => setPersonality({...personality, proactivity: v})} leftLabel="Minimal Nudges" rightLabel="Full Jarvis Mode" />
      </div>

      {/* Notifications */}
      <div style={{ padding:"20px", borderRadius:"14px", background:t.bgCard, border:`1px solid ${t.border}`, marginBottom:"16px" }}>
        <h3 style={{ fontSize:"13px", fontWeight:700, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"16px" }}>Notifications</h3>
        {["Morning Briefing", "Priority Messages", "Relationship Alerts", "Weekly Digest"].map(n => (
          <div key={n} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0" }}>
            <span style={{ fontSize:"13px" }}>{n}</span>
            <div style={{ width:"44px", height:"24px", borderRadius:"12px", background:t.accent, cursor:"pointer", position:"relative" }}>
              <div style={{ width:"20px", height:"20px", borderRadius:"50%", background:"#fff", position:"absolute", top:"2px", right:"2px", transition:"all 0.2s" }} />
            </div>
          </div>
        ))}
      </div>

      {/* Logout */}
      <button onClick={onLogout} style={{ width:"100%", padding:"14px", borderRadius:"12px", border:`1px solid ${t.danger}44`, background:t.dangerBg, color:t.danger, fontSize:"14px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:"8px" }}>
        <Icons.LogOut size={16} /> Sign Out
      </button>
    </div>
  );
}


// ============================================================================
// ADMIN PANEL
// ============================================================================

function AdminPanel({ onLogout }) {
  const t = useTheme();
  const [users] = useState([
    { id: "usr_001", name: "Marcus Rivera", email: "marcus.rivera@nexustest.com", role: "Sales Director", company: "TechVenture Solutions", status: "active", createdAt: "Feb 1, 2025", lastActive: "Just now" },
    { id: "usr_002", name: "Lisa Wong", email: "lisa.wong@demo.com", role: "Account Executive", company: "Pinnacle Group", status: "active", createdAt: "Feb 10, 2025", lastActive: "2 hours ago" },
    { id: "usr_003", name: "Raj Mehta", email: "raj.mehta@demo.com", role: "Founder", company: "DataLeap", status: "active", createdAt: "Feb 14, 2025", lastActive: "1 day ago" },
    { id: "usr_004", name: "Emily Cross", email: "emily.cross@demo.com", role: "Head of BD", company: "Stratagem Inc", status: "inactive", createdAt: "Feb 8, 2025", lastActive: "1 week ago" },
  ]);

  return (
    <div style={{ minHeight:"100vh", background:t.gradient }}>
      <div style={{ maxWidth:"1000px", margin:"0 auto", padding:"32px 24px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"32px" }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"4px" }}>
              <div style={{ width:"36px", height:"36px", borderRadius:"10px", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <Icons.Shield size={18} />
              </div>
              <h1 style={{ fontSize:"22px", fontWeight:700 }}>Admin Dashboard</h1>
            </div>
            <p style={{ fontSize:"13px", color:t.textSecondary, marginLeft:"48px" }}>User management & access control — No user data access</p>
          </div>
          <button onClick={onLogout} style={{ padding:"8px 18px", borderRadius:"10px", border:`1px solid ${t.border}`, background:t.bgCard, color:t.textSecondary, fontSize:"12px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:"6px" }}>
            <Icons.LogOut size={14} /> Sign Out
          </button>
        </div>

        {/* Stats */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:"14px", marginBottom:"28px" }}>
          {[
            { label: "Total Users", value: users.length, color: t.accent },
            { label: "Active", value: users.filter(u => u.status === "active").length, color: t.success },
            { label: "Inactive", value: users.filter(u => u.status === "inactive").length, color: t.textMuted },
            { label: "This Month", value: "+3", color: t.accentSecondary },
          ].map((s, i) => (
            <div key={i} style={{ padding:"20px", borderRadius:"14px", background:t.bgCard, border:`1px solid ${t.border}`, boxShadow:t.cardGlow }}>
              <div style={{ fontSize:"10px", fontWeight:700, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"8px" }}>{s.label}</div>
              <div style={{ fontSize:"28px", fontWeight:800, color:s.color, fontFamily:"'JetBrains Mono', monospace" }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* User Table */}
        <div style={{ borderRadius:"14px", background:t.bgCard, border:`1px solid ${t.border}`, overflow:"hidden", boxShadow:t.cardGlow }}>
          <div style={{ padding:"18px 20px", borderBottom:`1px solid ${t.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <h3 style={{ fontSize:"14px", fontWeight:700 }}>Users</h3>
            <span style={{ fontSize:"12px", color:t.textMuted }}>Data access: None — Metadata only</span>
          </div>
          <div style={{ overflow:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr>
                  {["User", "Email", "Company", "Status", "Created", "Last Active", "Actions"].map(h => (
                    <th key={h} style={{ padding:"12px 16px", textAlign:"left", fontSize:"11px", fontWeight:700, color:t.textMuted, textTransform:"uppercase", letterSpacing:"0.05em", borderBottom:`1px solid ${t.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom:`1px solid ${t.borderLight}` }}>
                    <td style={{ padding:"14px 16px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                        <div style={{ width:"32px", height:"32px", borderRadius:"8px", background:`linear-gradient(135deg, ${t.accent}30, ${t.accentSecondary}30)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"11px", fontWeight:700, color:t.accent }}>
                          {u.name.split(" ").map(w => w[0]).join("")}
                        </div>
                        <div>
                          <div style={{ fontSize:"13px", fontWeight:600 }}>{u.name}</div>
                          <div style={{ fontSize:"11px", color:t.textMuted }}>{u.role}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:"14px 16px", fontSize:"12px", color:t.textSecondary }}>{u.email}</td>
                    <td style={{ padding:"14px 16px", fontSize:"12px", color:t.textSecondary }}>{u.company}</td>
                    <td style={{ padding:"14px 16px" }}>
                      <span style={{ padding:"4px 10px", borderRadius:"6px", fontSize:"11px", fontWeight:700,
                        background: u.status === "active" ? `${t.success}18` : `${t.textMuted}18`,
                        color: u.status === "active" ? t.success : t.textMuted, textTransform:"capitalize" }}>
                        {u.status}
                      </span>
                    </td>
                    <td style={{ padding:"14px 16px", fontSize:"12px", color:t.textMuted }}>{u.createdAt}</td>
                    <td style={{ padding:"14px 16px", fontSize:"12px", color:t.textMuted }}>{u.lastActive}</td>
                    <td style={{ padding:"14px 16px" }}>
                      <button style={{ padding:"5px 12px", borderRadius:"6px", border:`1px solid ${t.border}`, background:"transparent", color: u.status === "active" ? t.danger : t.success, fontSize:"11px", fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                        {u.status === "active" ? "Disable" : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ marginTop:"20px", padding:"16px", borderRadius:"10px", background:t.bgCard, border:`1px solid ${t.border}`, display:"flex", alignItems:"center", gap:"10px" }}>
          <Icons.Shield size={16} />
          <span style={{ fontSize:"12px", color:t.textSecondary }}>Privacy Notice: Admin can manage user access only. No user messages, contacts, or Nexus conversations are accessible from this panel.</span>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// MAIN APP SHELL
// ============================================================================

function AppShell({ user, onLogout, onThemeChange, currentTheme }) {
  const t = useTheme();
  const [activeTab, setActiveTab] = useState("nexus");

  const tabs = [
    { id: "inbox", label: "Inbox", icon: Icons.Inbox, badge: 4 },
    { id: "contacts", label: "Contacts", icon: Icons.Users, badge: 0 },
    { id: "nexus", label: "Nexus", icon: Icons.Sparkles, badge: 0 },
    { id: "settings", label: "Settings", icon: Icons.Settings, badge: 0 },
  ];

  return (
    <div style={{ display:"flex", height:"100vh", background:t.bg }}>
      {/* Sidebar */}
      <div style={{ width:"72px", background:t.bgSidebar, borderRight:`1px solid ${t.border}`, display:"flex", flexDirection:"column", alignItems:"center", paddingTop:"16px", paddingBottom:"16px", flexShrink:0 }}>
        <div style={{ width:"40px", height:"40px", borderRadius:"12px", background:`linear-gradient(135deg, ${t.accent}, ${t.accentSecondary})`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:"24px", cursor:"pointer", boxShadow:`0 2px 12px ${t.accentGlow}` }}>
          <Icons.Zap size={20} />
        </div>

        <div style={{ flex:1, display:"flex", flexDirection:"column", gap:"6px" }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{ width:"48px", height:"48px", borderRadius:"12px", border:"none", background: active ? t.accentGlow : "transparent", color: active ? t.accent : t.textMuted, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", transition:"all 0.2s" }}
                title={tab.label}>
                <Icon size={20} />
                {tab.badge > 0 && (
                  <div style={{ position:"absolute", top:"6px", right:"6px", width:"16px", height:"16px", borderRadius:"50%", background:t.accent, color:"#fff", fontSize:"9px", fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", border:`2px solid ${t.bgSidebar}` }}>
                    {tab.badge}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <button onClick={onLogout} style={{ width:"40px", height:"40px", borderRadius:"10px", border:"none", background:"transparent", color:t.textMuted, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }} title="Sign Out">
          <Icons.LogOut size={18} />
        </button>
      </div>

      {/* Main Content */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
        {/* Top Bar */}
        <div style={{ height:"56px", borderBottom:`1px solid ${t.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 24px", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
            <h1 style={{ fontSize:"16px", fontWeight:700 }}>
              {tabs.find(t => t.id === activeTab)?.label}
            </h1>
            {activeTab === "nexus" && (
              <span style={{ padding:"3px 10px", borderRadius:"6px", fontSize:"10px", fontWeight:700, background:`linear-gradient(135deg, ${t.accent}20, ${t.accentSecondary}20)`, color:t.accent, letterSpacing:"0.05em" }}>AI AGENT</span>
            )}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
            <button style={{ width:"34px", height:"34px", borderRadius:"8px", border:`1px solid ${t.border}`, background:"transparent", color:t.textSecondary, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", position:"relative" }}>
              <Icons.Bell size={16} />
              <div style={{ position:"absolute", top:"5px", right:"5px", width:"7px", height:"7px", borderRadius:"50%", background:t.accent }} />
            </button>
            <div style={{ width:"32px", height:"32px", borderRadius:"8px", background:`linear-gradient(135deg, ${t.accent}30, ${t.accentSecondary}30)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"11px", fontWeight:700, color:t.accent, cursor:"pointer" }}>
              {user.name?.split(" ").map(w => w[0]).join("") || "?"}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex:1, overflow:"hidden" }}>
          {activeTab === "inbox" && <UnifiedInbox />}
          {activeTab === "contacts" && <ContactsView />}
          {activeTab === "nexus" && <NexusAgent />}
          {activeTab === "settings" && <SettingsView user={user} onLogout={onLogout} onThemeChange={onThemeChange} currentTheme={currentTheme} />}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// ROOT APP
// ============================================================================

export default function NexusAssistant() {
  const [user, setUser] = useState(null);
  const [themeName, setThemeName] = useState("midnight");
  const [showOnboarding, setShowOnboarding] = useState(false);

  const t = themes[themeName];

  const handleLogin = (userData) => {
    setUser(userData);
    if (userData.isAdmin) return;
    if (userData.theme) setThemeName(userData.theme);
    if (!userData.onboardingComplete) {
      setShowOnboarding(true);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setShowOnboarding(false);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setUser(prev => ({ ...prev, onboardingComplete: true, linkedinConnected: true, emailConnected: true }));
  };

  const handleThemeChange = (newTheme) => {
    setThemeName(newTheme);
  };

  return (
    <ThemeContext.Provider value={t}>
      <AuthContext.Provider value={{ user, login: handleLogin, logout: handleLogout }}>
        <style>{getStyles(t)}</style>

        {!user && <LoginScreen onLogin={handleLogin} />}

        {user && user.isAdmin && <AdminPanel onLogout={handleLogout} />}

        {user && !user.isAdmin && showOnboarding && (
          <OnboardingWizard user={user} onComplete={handleOnboardingComplete} />
        )}

        {user && !user.isAdmin && !showOnboarding && (
          <AppShell user={user} onLogout={handleLogout} onThemeChange={handleThemeChange} currentTheme={themeName} />
        )}
      </AuthContext.Provider>
    </ThemeContext.Provider>
  );
}
